"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, type TFn } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/navigation";
import { imageUrl } from "@/lib/image-url";
import { characterHex, textColor } from "@/lib/character-colors";
import type { FastestBlock, RunRow } from "./HomeLeaderboardSection";
import { dedupePartyRows } from "@/lib/party-dedupe";
import { PlayerBadge } from "@/app/components/SupporterBadge";
const TARGET_ASCENSION = 10;
const POLL_MS = 20_000;
const ARROW = <svg className="arw" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>;
function characterIcon(character: string): string {
  return imageUrl(`/static/images/characters/character_icon_${character.toLowerCase()}.webp`);
}
const ENGLISH_CHARACTER_LABELS: Record<string, string> = {
  IRONCLAD: "Ironclad",
  SILENT: "Silent",
  DEFECT: "Defect",
  NECROBINDER: "Necrobinder",
  REGENT: "Regent"
};

/** Resolve a character key (uppercase from the runs API: `IRONCLAD`,
 * `SILENT`, etc.) to its localized display name. The translations API
 * keys characters in lowercase, so we lowercase before looking up.
 * Falls back to the English label, then a title-cased raw key. */
function characterLabel(c: string, names?: Record<string, string>): string {
  return names?.[c.toLowerCase()] ?? ENGLISH_CHARACTER_LABELS[c] ?? c.charAt(0) + c.slice(1).toLowerCase();
}
function formatRunTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}
function formatRelativeDate(submittedAt: string, t: TFn): string {
  // submitted_at is `YYYY-MM-DD HH:MM:SS` UTC. Treat as UTC then diff.
  const d = new Date(submittedAt.replace(" ", "T") + "Z");
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return t("just now");
  const min = Math.floor(sec / 60);
  if (min < 60) return t("{n}m ago", {
    n: min
  });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("{n}h ago", {
    n: hr
  });
  const day = Math.floor(hr / 24);
  if (day < 30) return t("{n}d ago", {
    n: day
  });
  const mo = Math.floor(day / 30);
  if (mo < 12) return t("{n}mo ago", {
    n: mo
  });
  return t("{n}y ago", {
    n: Math.floor(mo / 12)
  });
}
function killedByLabel(killedBy: string | null): string | null {
  if (!killedBy) return null;
  // KNOWLEDGE_DEMON_BOSS → Knowledge Demon
  return killedBy.replace(/_BOSS$/i, "").toLowerCase().split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
export default function HomeLeaderboardLive(t0) {
  const $ = _c(90);
  const {
    initialFastest,
    initialDaily,
    initialRecent,
    lang: t1,
    characterNames,
    runsHost,
    pollBase
  } = t0;
  t1 === undefined ? "eng" : t1;
  const t = useT();
  const [fastest, setFastest] = useState(initialFastest);
  const [daily, setDaily] = useState(initialDaily);
  const [recent, setRecent] = useState(initialRecent);
  let t2;
  let t3;
  if ($[0] !== pollBase) {
    t2 = () => {
      let active = true;
      const grab = _temp3;
      const load = () => {
        if (document.hidden) {
          return;
        }
        grab(`${pollBase}/api/runs/leaderboard?category=fastest&ascension_min=${TARGET_ASCENSION}&players=single&game_mode=standard&limit=5`).then(d => {
          if (active && d?.runs) {
            const runs = (d.runs as RunRow[]).filter(_temp4).slice(0, 5);
            if (runs.length) {
              setFastest({
                runs,
                ascension: TARGET_ASCENSION
              });
            }
          }
        });
        grab(`${pollBase}/api/runs/leaderboard?category=highest_ascension&game_mode=daily&today=true&players=single&limit=20`).then(d_0 => {
          if (active && d_0?.runs) {
            setDaily(dedupePartyRows(d_0.runs as RunRow[]).slice(0, 5));
          }
        });
        grab(`${pollBase}/api/runs/list?limit=5&sort=newest`).then(d_1 => {
          if (active && d_1?.runs) {
            setRecent(d_1.runs as RunRow[]);
          }
        });
      };
      const timer = setInterval(load, POLL_MS);
      const onVisible = () => {
        if (!document.hidden) {
          load();
        }
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        active = false;
        clearInterval(timer);
        document.removeEventListener("visibilitychange", onVisible);
      };
    };
    t3 = [pollBase];
    $[0] = pollBase;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const lbBase = `${runsHost}/leaderboards`;
  const runsBase = `${runsHost}/runs`;
  const ascLabel = fastest.ascension === TARGET_ASCENSION ? `A${TARGET_ASCENSION}` : fastest.ascension !== null ? `A${fastest.ascension}` : null;
  let t4;
  if ($[3] !== t) {
    t4 = t("Leaderboards");
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <h2>{t4}</h2>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `${lbBase}/submit`;
  let t7;
  if ($[7] !== t) {
    t7 = t("Upload your runs");
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <Link prefetch={false} className="viewmore" href={t6}>{t7} {ARROW}</Link>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t5 || $[13] !== t8) {
    t9 = <div className="s-head">{t5}{t8}</div>;
    $[12] = t5;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== ascLabel) {
    t10 = ascLabel && <span className="s-kick">{ascLabel}</span>;
    $[15] = ascLabel;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t) {
    t11 = t("Fastest Wins");
    $[17] = t;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11) {
    t12 = <h2>{t11}</h2>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t) {
    t13 = t("View more");
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== runsBase || $[24] !== t13) {
    t14 = <Link prefetch={false} className="viewmore" href={runsBase}>{t13} {ARROW}</Link>;
    $[23] = runsBase;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t14) {
    t15 = <div className="s-head">{t10}{t12}{t14}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== characterNames || $[31] !== fastest.runs || $[32] !== runsBase || $[33] !== t) {
    t16 = fastest.runs.length === 0 ? <p className="lb-empty">{t("No A10 wins submitted yet, be the first.")}</p> : <div className="overflow-x-auto"><table className="dtable"><thead><tr><th className="rk">#</th><th>{t("Character")}</th><th className="num">{t("Asc")}</th><th className="num">{t("Time")}</th></tr></thead><tbody>{fastest.runs.map((r_1, i) => <tr key={r_1.run_hash}><td className="rk">{i + 1}</td><td><Link prefetch={false} className="ent" href={`${runsBase}/${r_1.run_hash}`}><img crossOrigin="anonymous" className="lb-ico" src={characterIcon(r_1.character)} alt={characterLabel(r_1.character, characterNames)} loading="lazy" /><span className="lb-who"><span className="lb-name" style={{
                    color: characterHex(r_1.character) ? textColor(characterHex(r_1.character)) : undefined
                  }}>{characterLabel(r_1.character, characterNames)}</span><span className="lb-sub">{r_1.username ?? t("Anonymous")}<PlayerBadge username={r_1.username} className="ml-1" />{" "}· fl{r_1.floors_reached}</span></span></Link></td><td className="num">A{r_1.ascension}</td><td className="num mono">{formatRunTime(r_1.run_time)}</td></tr>)}</tbody></table></div>;
    $[30] = characterNames;
    $[31] = fastest.runs;
    $[32] = runsBase;
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <section className="panel">{t15}{t16}</section>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("resets 00:00 UTC");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <span className="s-kick">{t18}</span>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t) {
    t20 = t("Daily Climb");
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <h2>{t20}</h2>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = `${runsBase}?win=true&game_mode=daily_today&sort=ascension_desc`;
  let t23;
  if ($[46] !== t) {
    t23 = t("View more");
    $[46] = t;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t22 || $[49] !== t23) {
    t24 = <Link prefetch={false} className="viewmore" href={t22}>{t23} {ARROW}</Link>;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t19 || $[52] !== t21 || $[53] !== t24) {
    t25 = <div className="s-head">{t19}{t21}{t24}</div>;
    $[51] = t19;
    $[52] = t21;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== characterNames || $[56] !== daily || $[57] !== runsBase || $[58] !== t) {
    t26 = daily.length === 0 ? <p className="lb-empty">{t("No daily runs yet today.")}</p> : <div className="overflow-x-auto"><table className="dtable"><thead><tr><th className="rk">#</th><th>{t("Character")}</th><th className="num">{t("Asc")}</th><th className="num">{t("Time")}</th></tr></thead><tbody>{daily.map((r_2, i_0) => <tr key={r_2.run_hash}><td className="rk">{i_0 + 1}</td><td><Link prefetch={false} className="ent" href={`${runsBase}/${r_2.run_hash}`}><img crossOrigin="anonymous" className="lb-ico" src={characterIcon(r_2.character)} alt={characterLabel(r_2.character, characterNames)} loading="lazy" /><span className="lb-who"><span className="lb-name" style={{
                    color: characterHex(r_2.character) ? textColor(characterHex(r_2.character)) : undefined
                  }}>{characterLabel(r_2.character, characterNames)}</span><span className="lb-sub">{r_2.username ?? t("Anonymous")}<PlayerBadge username={r_2.username} className="ml-1" />{" "}· fl{r_2.floors_reached}</span></span></Link></td><td className="num">A{r_2.ascension}</td><td className="num mono">{formatRunTime(r_2.run_time)}</td></tr>)}</tbody></table></div>;
    $[55] = characterNames;
    $[56] = daily;
    $[57] = runsBase;
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t25 || $[61] !== t26) {
    t27 = <section className="panel">{t25}{t26}</section>;
    $[60] = t25;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t) {
    t28 = t("Recent Runs");
    $[63] = t;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t28) {
    t29 = <h2>{t28}</h2>;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t) {
    t30 = t("View more");
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== runsBase || $[70] !== t30) {
    t31 = <Link prefetch={false} className="viewmore" href={runsBase}>{t30} {ARROW}</Link>;
    $[69] = runsBase;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t29 || $[73] !== t31) {
    t32 = <div className="s-head">{t29}{t31}</div>;
    $[72] = t29;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== characterNames || $[76] !== recent || $[77] !== runsBase || $[78] !== t) {
    t33 = recent.length === 0 ? <p className="lb-empty">{t("No runs submitted yet.")}</p> : <table className="dtable dtable-fixed"><thead><tr><th>{t("Character")}</th><th className="num" style={{
            width: "2.75rem"
          }}>{t("Result")}</th><th className="num" style={{
            width: "5rem"
          }}>{t("When")}</th></tr></thead><tbody>{recent.map(r_3 => {
          const result = r_3.win ? "win" : r_3.was_abandoned ? "abandoned" : "loss";
          const killer = killedByLabel(r_3.killed_by);
          const killerShort = killer && killer.length > 10 ? killer.slice(0, 10).trimEnd() + "\u2026" : killer;
          return <tr key={r_3.run_hash}><td><Link prefetch={false} className="ent" href={`${runsBase}/${r_3.run_hash}`}><img crossOrigin="anonymous" className="lb-ico" src={characterIcon(r_3.character)} alt={characterLabel(r_3.character, characterNames)} loading="lazy" /><span className="lb-who"><span className="lb-name" style={{
                    color: characterHex(r_3.character) ? textColor(characterHex(r_3.character)) : undefined
                  }}>{characterLabel(r_3.character, characterNames)}<span className="dim"> A{r_3.ascension}</span></span><span className="lb-sub">fl{r_3.floors_reached} ·{" "}{formatRunTime(r_3.run_time)}{killerShort && result === "loss" ? ` · ${t("died to {name}", {
                      name: killerShort
                    })}` : ""}</span></span></Link></td><td className="num">{result === "win" && <span className="wr-sg" title={t("Win")}>W</span>}{result === "loss" && <span className="wr-loss" title={t("Loss")}>L</span>}{result === "abandoned" && <span className="dim" title={t("Abandoned")}>A</span>}</td><td className="num dim">{formatRelativeDate(r_3.submitted_at, t)}</td></tr>;
        })}</tbody></table>;
    $[75] = characterNames;
    $[76] = recent;
    $[77] = runsBase;
    $[78] = t;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t32 || $[81] !== t33) {
    t34 = <section className="panel">{t32}{t33}</section>;
    $[80] = t32;
    $[81] = t33;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== t17 || $[84] !== t27 || $[85] !== t34) {
    t35 = <div className="lbgrid">{t17}{t27}{t34}</div>;
    $[83] = t17;
    $[84] = t27;
    $[85] = t34;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== t35 || $[88] !== t9) {
    t36 = <div className="rvmp"><section className="hb"><div className="hsec">{t9}{t35}</div></section></div>;
    $[87] = t35;
    $[88] = t9;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  return t36;
}
function _temp4(r_0) {
  return r_0.win === 1;
}
function _temp3(url) {
  return fetch(url).then(_temp).catch(_temp2);
}
function _temp2() {
  return null;
}
function _temp(r) {
  return r.ok ? r.json() : null;
}